(() => {
  if (customElements.get('edc-particle-network')) return;

  class EdcParticleNetwork extends HTMLElement {
    connectedCallback() {
      this.canvas = this.querySelector('canvas');
      this.context = this.canvas?.getContext('2d');
      if (!this.context) return;

      this.controller = new AbortController();
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.particles = [];
      this.highlights = [];
      this.highlightDelay = 0;
      this.visible = false;
      this.frame = null;
      this.lastTime = null;
      this.resize = this.resize.bind(this);
      this.tick = this.tick.bind(this);
      this.sync = this.sync.bind(this);
      const options = { signal: this.controller.signal };
      window.addEventListener('resize', this.resize, options);
      document.addEventListener('visibilitychange', this.sync, options);
      this.motion.addEventListener('change', this.sync, options);
      this.resize();
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.sync();
      });
      this.observer.observe(this.parentElement);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.stop();
    }

    stop() {
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.lastTime = null;
    }

    sync() {
      this.stop();
      if (!this.context || !this.visible || document.hidden) return;
      this.draw();
      if (!this.motion.matches) this.frame = requestAnimationFrame(this.tick);
    }

    resize() {
      this.width = Math.max(1, this.clientWidth);
      this.height = Math.max(1, this.clientHeight);
      const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
      this.canvas.width = Math.round(this.width * ratio);
      this.canvas.height = Math.round(this.height * ratio);
      this.context.setTransform(ratio, 0, 0, ratio, 0, 0);
      this.highlightColor = getComputedStyle(this).getPropertyValue('--edc-color-highlight').trim();
      const count = Math.min(280, Math.max(40, Math.round(this.width * this.height / 6000)));
      this.particles.length = Math.min(this.particles.length, count);
      while (this.particles.length < count) {
        const angle = Math.random() * Math.PI * 2;
        this.particles.push({
          x: Math.random(), y: Math.random(),
          vx: Math.cos(angle) * 7, vy: Math.sin(angle) * 7,
          radius: 0.8 + Math.random() * 0.6,
        });
      }
      this.highlights = this.highlights.filter(({ particle }) => this.particles.includes(particle));
      this.sync();
    }

    tick(time) {
      this.frame = null;
      if (!this.visible || document.hidden || this.motion.matches) return;
      if (this.lastTime === null) this.lastTime = time;
      const elapsed = time - this.lastTime;
      if (elapsed >= 1000 / 30) {
        const delta = Math.min(elapsed / 1000, 0.08);
        this.lastTime = time;
        this.updateHighlights(delta);
        for (const particle of this.particles) {
          particle.x += particle.vx * delta / this.width;
          particle.y += particle.vy * delta / this.height;
          if (particle.x < 0 || particle.x > 1) {
            particle.x = Math.max(0, Math.min(1, particle.x));
            particle.vx *= -1;
          }
          if (particle.y < 0 || particle.y > 1) {
            particle.y = Math.max(0, Math.min(1, particle.y));
            particle.vy *= -1;
          }
        }
        this.draw();
      }
      this.frame = requestAnimationFrame(this.tick);
    }

    updateHighlights(delta) {
      for (const highlight of this.highlights) highlight.age += delta;
      this.highlights = this.highlights.filter(({ age, duration }) => age < duration);
      this.highlightDelay -= delta;
      if (this.highlightDelay > 0) return;
      this.highlightDelay = 0.35 + Math.random() * 0.45;
      const limit = this.width < 750 ? 5 : 10;
      if (this.highlights.length >= limit) return;
      const candidates = this.particles.filter((particle) =>
        !this.highlights.some((highlight) => highlight.particle === particle));
      if (!candidates.length) return;
      this.highlights.push({
        particle: candidates[Math.floor(Math.random() * candidates.length)],
        age: 0,
        duration: 5 + Math.random() * 3,
      });
    }

    draw() {
      const ctx = this.context;
      ctx.clearRect(0, 0, this.width, this.height);
      const reach = Math.min(240, this.width * 0.5);
      const reachSquared = reach * reach;
      ctx.lineWidth = 0.65;
      for (let i = 0; i < this.particles.length; i += 1) {
        const a = this.particles[i];
        const x = a.x * this.width;
        const y = a.y * this.height;
        for (let j = i + 1; j < this.particles.length; j += 1) {
          const b = this.particles[j];
          const bx = b.x * this.width;
          const by = b.y * this.height;
          const distanceSquared = (x - bx) ** 2 + (y - by) ** 2;
          if (distanceSquared >= reachSquared) continue;
          const opacity = (1 - Math.sqrt(distanceSquared) / reach) * 0.13;
          ctx.strokeStyle = `rgba(25,25,25,${opacity})`;
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(bx, by);
          ctx.stroke();
        }
        ctx.fillStyle = 'rgba(25,25,25,0.18)';
        ctx.beginPath();
        ctx.arc(x, y, a.radius, 0, Math.PI * 2);
        ctx.fill();
      }
      if (this.motion.matches || !this.highlightColor) return;
      ctx.fillStyle = this.highlightColor;
      for (const { particle, age, duration } of this.highlights) {
        const intensity = Math.min(1, age / 0.7, (duration - age) / 1.2);
        const x = particle.x * this.width;
        const y = particle.y * this.height;
        ctx.globalAlpha = intensity * 0.3;
        ctx.beginPath();
        ctx.arc(x, y, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = intensity;
        ctx.beginPath();
        ctx.arc(x, y, particle.radius + 2.2, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    }
  }

  customElements.define('edc-particle-network', EdcParticleNetwork);
})();
